/**
 * SSE Connection Manager
 *
 * Manages Server-Sent Events connection with auto-reconnection.
 * Uses fetch() + ReadableStream to support custom headers (x-api-key).
 */

import { SSE_CONFIG } from '../utils/constants';
import type {
  SSEConfig,
  SSEConnectionState,
  SSEEvent,
  PaymentProgressData,
  FieldProgressData,
  FieldCollectedData,
  FieldCompletedData,
  FieldValidationFailedData,
  VoiceManualRecommendedData,
  PaymentCompleteData,
  PaymentFailedData,
  FieldResetData,
  VoiceManualInitiatedData,
  SecureModeEnteredData,
  SessionUpdatedData,
  SessionFailedData,
  TimelineEventData,
} from '../types/sse.types';
import { logger } from '../utils/logger';

/**
 * SSE Connection Class
 *
 * Handles:
 * - fetch() + ReadableStream connection with custom headers
 * - Manual SSE parsing (event:, data:, id:)
 * - Event routing to handlers
 * - Auto-reconnection with exponential backoff
 * - Connection state management
 */
// Abort fetch if the server doesn't respond within this window.
// 35s gives the backend time to flush the 200 + first heartbeat (sent every 30s)
// before we give up. Must stay above the backend heartbeat interval (30s) and
// below the CloudFront hard 504 limit (~60s).
const CONNECT_TIMEOUT_MS = 35_000;

export class SSEConnection {
  private abortController: AbortController | null = null;
  private reader: ReadableStreamDefaultReader<string> | null = null;
  private config: SSEConfig;
  private state: SSEConnectionState = 'disconnected';
  private reconnectAttempts = 0;
  private reconnectTimeoutId: number | null = null;
  private isManualDisconnect = false;
  private connectedAt: number | null = null;
  // Bumped on every successful connect and proactive rotation. Lets a
  // readStream() loop recognise, once its stream ends, whether it was
  // reading the connection that is still current — if not, it was
  // intentionally superseded by a rotation and should not trigger a
  // reconnect.
  private connectionId = 0;
  private rotationTimeoutId: number | null = null;
  // Connections that live longer than this threshold are considered healthy
  // and reset the reconnect counter on drop, allowing indefinite reconnection.
  // Set to 25s — just below the Lambda proxy 29s hard timeout — so that a
  // connection that received at least one heartbeat (backend sends every 30s)
  // is treated as healthy and the counter resets, preventing permanent failure.
  private static readonly MIN_HEALTHY_DURATION_MS = 25_000;
  // How long to wait before proactively opening a replacement connection
  // while the current one is still being read from. Set just below the
  // Lambda proxy's ~29s hard timeout, so the new connection is live and
  // receiving before the old one is force-closed — eliminating the "nobody
  // is listening" gap that exists when reconnection only starts reactively,
  // after the drop has already happened.
  private static readonly PROACTIVE_RECONNECT_MS = 25_000;

  /**
   * Create SSE connection manager
   *
   * @param config - SSE configuration
   */
  constructor(config: SSEConfig) {
    this.config = {
      reconnect: true,
      maxReconnectAttempts: SSE_CONFIG.MAX_RECONNECT_ATTEMPTS,
      ...config,
    };
  }

  /**
   * Connect to SSE endpoint using fetch() + ReadableStream
   *
   * Uses fetch() instead of EventSource to support custom headers
   * (x-api-key required by backend; sessionId passed as query param)
   */
  async connect(): Promise<void> {
    // Prevent multiple connections
    if (this.abortController) {
      logger.warn('SSE connection already exists');
      return;
    }

    this.isManualDisconnect = false;
    this.setState('connecting');

    const abortController = new AbortController();
    this.abortController = abortController;

    logger.info('SSE connecting', {
      endpoint: this.config.endpoint,
      sessionId: this.config.sessionId,
    });

    const result = await this.fetchSSEStream(abortController);

    if (!result.ok) {
      this.abortController = null;
      // Session expired (HTTP 410): terminal. Surface it to the component so it
      // can show the session-expired screen, and stop reconnecting — retrying
      // would only hit the same 410 in a loop (PAS-32663).
      if (result.sessionExpired) {
        this.setState('failed');
        this.config.handlers.onSessionExpired?.();
        this.disconnect();
        return;
      }
      // AbortError (manual disconnect, or the connect timeout above) is
      // swallowed — disconnect() already put us in the right state, and a
      // timed-out attempt is left as-is rather than retried here.
      if (!result.aborted) {
        this.setState('failed');
        this.handleConnectionError();
      }
      return;
    }

    const connectionId = ++this.connectionId;
    const reader = result.reader;
    this.reader = reader;

    // Connection established
    this.setState('connected');
    this.connectedAt = Date.now();
    logger.info('SSE connected');
    this.config.handlers.onOpen?.();

    this.scheduleProactiveRotation(connectionId);
    this.readStream(reader, connectionId);
  }

  /**
   * Open one HTTP + SSE connection.
   *
   * Shared by connect() (the primary connection) and rotateConnection()
   * (a proactive replacement opened ahead of the Lambda proxy cutoff).
   * Does not touch `this.abortController`/`this.reader` — callers decide
   * when the new connection becomes "the" connection.
   *
   * @returns The stream reader on success, or null on failure/abort.
   */
  private async fetchSSEStream(
    abortController: AbortController
  ): Promise<
    | { ok: true; reader: ReadableStreamDefaultReader<string> }
    | { ok: false; aborted: boolean; sessionExpired?: boolean }
  > {
    const url = this.buildSSEUrl();

    // Abort if the server doesn't respond within CONNECT_TIMEOUT_MS.
    // Without this, a 504 from CloudFront can take ~30s per attempt,
    // making the full reconnect cycle take many minutes.
    const connectTimeoutId = window.setTimeout(() => {
      abortController.abort();
    }, CONNECT_TIMEOUT_MS);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'x-api-key': this.config.apiKey,
          'x-session-id': this.config.sessionId,
        },
        signal: abortController.signal,
      });

      window.clearTimeout(connectTimeoutId);

      if (!response.ok) {
        // 410 Gone = the agent session has expired/was invalidated. This is a
        // terminal condition: reconnecting always hits the same 410, so signal
        // session-expiry instead of entering the generic reconnect loop
        // (the connection is refused, so no SSE SESSION_EXPIRED event arrives).
        if (response.status === 410) {
          logger.warn('SSE connection returned 410 — session expired');
          return { ok: false, aborted: false, sessionExpired: true };
        }
        throw new Error(
          `SSE connection failed: ${response.status} ${response.statusText}`
        );
      }

      if (!response.body) {
        throw new Error('SSE response has no body');
      }

      const reader = response.body
        .pipeThrough(new TextDecoderStream())
        .getReader();
      return { ok: true, reader };
    } catch (error) {
      window.clearTimeout(connectTimeoutId);
      const aborted = error instanceof Error && error.name === 'AbortError';
      // Only log error if not aborted (manual disconnect or connect timeout)
      if (!aborted) {
        logger.error('SSE connection failed', error);
      }
      return { ok: false, aborted };
    }
  }

  /**
   * Schedule a proactive connection rotation ahead of the Lambda cutoff.
   */
  private scheduleProactiveRotation(connectionId: number): void {
    this.rotationTimeoutId = window.setTimeout(() => {
      this.rotationTimeoutId = null;
      this.rotateConnection(connectionId);
    }, SSEConnection.PROACTIVE_RECONNECT_MS);
  }

  /**
   * Proactively open a replacement connection while the current one is
   * still being read from, then swap to it once it is confirmed live.
   *
   * This is what closes the "nobody is listening" gap: today, reconnection
   * only starts after the old connection has already ended. By opening the
   * new one first and only tearing down the old one once the new one is
   * live, there is (outside of a genuine unexpected failure) no window
   * where a backend-emitted event has no connection to land on.
   */
  private async rotateConnection(previousConnectionId: number): Promise<void> {
    // Stale timer — connection already gone (manual disconnect) or already
    // rotated/reconnected past this generation. Nothing to do.
    if (this.isManualDisconnect || this.connectionId !== previousConnectionId) {
      return;
    }

    logger.info('SSE proactively rotating connection before Lambda cutoff');

    const abortController = new AbortController();
    const result = await this.fetchSSEStream(abortController);

    if (
      !result.ok ||
      this.isManualDisconnect ||
      this.connectionId !== previousConnectionId
    ) {
      // Swap failed, or things moved on while we were opening the new
      // connection. Leave the existing connection alone — if it later dies
      // for real, the normal reactive reconnect path takes over.
      abortController.abort();
      if (result.ok) {
        result.reader.cancel().catch(() => {});
      }
      return;
    }

    const oldAbortController = this.abortController;
    const oldReader = this.reader;

    const connectionId = ++this.connectionId;
    const reader = result.reader;
    this.abortController = abortController;
    this.reader = reader;
    this.connectedAt = Date.now();

    // Tear down the old connection now that the new one is confirmed live.
    // Bumping connectionId above lets the old readStream() loop recognise
    // this as an intentional supersession, not a failure.
    oldAbortController?.abort();
    oldReader?.cancel().catch(() => {});

    logger.info('SSE connection rotated proactively');
    this.scheduleProactiveRotation(connectionId);
    this.readStream(reader, connectionId);
  }

  /**
   * Disconnect from SSE endpoint
   *
   * Closes connection and prevents auto-reconnection
   */
  disconnect(): void {
    this.isManualDisconnect = true;

    // Clear reconnect timeout
    if (this.reconnectTimeoutId !== null) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Clear proactive rotation timeout
    if (this.rotationTimeoutId !== null) {
      clearTimeout(this.rotationTimeoutId);
      this.rotationTimeoutId = null;
    }

    // Cancel fetch request
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    // Release reader
    if (this.reader) {
      this.reader.cancel().catch(() => {});
      this.reader = null;
    }

    this.connectedAt = null;
    this.setState('disconnected');
    logger.info('SSE disconnected');
  }

  /**
   * Get current connection state
   *
   * @returns Current state
   */
  getState(): SSEConnectionState {
    return this.state;
  }

  /**
   * Build SSE URL with sessionId query param
   *
   * Authentication sent via x-api-key header; sessionId passed as query param
   *
   * @returns URL with sessionId query param
   */
  private buildSSEUrl(): string {
    const url = new URL(this.config.endpoint, window.location.origin);

    // Add sessionId as query param (required by backend)
    url.searchParams.set('sessionId', this.config.sessionId);

    return url.toString();
  }

  /**
   * Read SSE stream and parse events
   *
   * Parses SSE format manually:
   * event: EVENT_TYPE
   * data: {"eventType":"...","data":{...}}
   * (blank line)
   */
  private async readStream(
    reader: ReadableStreamDefaultReader<string>,
    connectionId: number
  ): Promise<void> {
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          // Drain any buffered data that arrived without a trailing blank line.
          // Backends that close the TCP connection immediately after the final
          // event may omit the terminating \n\n, leaving the event in `buffer`
          // unparsed. Process it now so no event (e.g. PAYMENT_COMPLETED) is lost.
          if (buffer.trim()) {
            this.parseSSEEvent(buffer);
            buffer = '';
          }

          // This stream was proactively superseded by a rotation — the
          // replacement connection is already live, so this is not a
          // failure and must not trigger the reactive reconnect path.
          if (this.connectionId !== connectionId) {
            return;
          }

          const durationMs =
            this.connectedAt !== null ? Date.now() - this.connectedAt : 0;
          const wasHealthy =
            durationMs >= SSEConnection.MIN_HEALTHY_DURATION_MS;
          logger.info('SSE stream ended', { durationMs, wasHealthy });
          // Only reset the counter for healthy long-lived connections.
          // Short drops (e.g. Lambda 30s proxy timeout) must accumulate toward
          // MAX_RECONNECT_ATTEMPTS so the error screen eventually appears.
          if (wasHealthy) this.reconnectAttempts = 0;
          this.connectedAt = null;
          this.handleConnectionError();
          break;
        }

        // Append chunk to buffer
        buffer += value;

        // Process complete events (separated by double newline)
        const events = buffer.split('\n\n');

        // Keep incomplete event in buffer
        buffer = events.pop() || '';

        // Parse each complete event
        for (const eventText of events) {
          if (eventText.trim()) {
            this.parseSSEEvent(eventText);
          }
        }
      }
    } catch (error) {
      // Superseded by a rotation — same reasoning as the `done` branch above.
      if (this.connectionId !== connectionId) {
        return;
      }
      // Only handle error if not manually disconnected
      if (!this.isManualDisconnect) {
        logger.error('SSE stream error', error);
        this.connectedAt = null;
        this.handleConnectionError();
      }
    }
  }

  /**
   * Parse SSE event format
   *
   * Format:
   * event: PAYMENT_PROGRESS
   * data: {"eventType":"PAYMENT_PROGRESS","sessionId":"...","timestamp":"...","data":{...}}
   *
   * @param eventText - Raw SSE event text
   */
  private parseSSEEvent(eventText: string): void {
    const lines = eventText.split('\n');
    let eventType: string | null = null;
    let dataStr: string | null = null;

    for (const line of lines) {
      if (line.startsWith('event:')) {
        eventType = line.substring(6).trim();
      } else if (line.startsWith('data:')) {
        dataStr = line.substring(5).trim();
      }
      // Ignore other fields (id:, retry:, comments starting with :)
    }

    if (!eventType || !dataStr) {
      logger.warn('Invalid SSE event format', { eventText });
      return;
    }

    try {
      const eventData = JSON.parse(dataStr);
      // Backend sends 'payload'; frontend handlers expect 'data' — normalise here
      if (eventData.payload !== undefined && eventData.data === undefined) {
        eventData.data = eventData.payload;
      }
      this.routeEvent(eventType, eventData);
    } catch (error) {
      logger.error('Failed to parse SSE event data', error, {
        eventType,
        dataStr,
      });
      this.config.handlers.onError?.(
        error instanceof Error ? error : new Error('Parse error')
      );
    }
  }

  /**
   * Route SSE event to appropriate handler
   *
   * Backend sends UPPERCASE event types (PAYMENT_PROGRESS, FIELD_COLLECTED, etc.)
   *
   * @param eventType - Event type from SSE stream (UPPERCASE)
   * @param eventData - Parsed event data
   */
  private routeEvent(eventType: string, eventData: SSEEvent<unknown>): void {
    logger.debug('SSE event received', {
      eventType,
      sessionId: eventData.sessionId,
    });

    // Route to appropriate handler based on event type
    switch (eventType) {
      case 'PAYMENT_PROGRESS':
        this.config.handlers.onProgress?.(
          eventData.data as PaymentProgressData
        );
        break;

      case 'PAYMENT_SUBMITTED':
        // Backend sends this but we don't have specific handler yet
        logger.info('Payment submitted', eventData.data);
        break;

      case 'FIELD_PROGRESS':
        this.config.handlers.onFieldProgress?.(
          eventData.data as FieldProgressData
        );
        break;

      case 'FIELD_COLLECTED':
        this.config.handlers.onFieldCollected?.(
          eventData.data as FieldCollectedData
        );
        break;

      case 'FIELD_VALIDATION_FAILED':
        this.config.handlers.onFieldValidationFailed?.(
          eventData.data as FieldValidationFailedData
        );
        break;

      case 'VOICE_MANUAL_RECOMMENDED':
        this.config.handlers.onVoiceManualRecommended?.(
          eventData.data as VoiceManualRecommendedData
        );
        break;

      case 'VOICE_MANUAL_INITIATED':
      case 'FALLBACK_TO_MANUAL_MODE':
        this.config.handlers.onVoiceManualInitiated?.(
          eventData.data as VoiceManualInitiatedData
        );
        break;

      case 'FIELD_COMPLETED':
        // CHAT field completion event from Payment Checkout UI
        this.config.handlers.onFieldCompleted?.(
          eventData.data as FieldCompletedData
        );
        break;

      case 'PAYMENT_COMPLETED':
        this.config.handlers.onPaymentComplete?.(
          eventData.data as PaymentCompleteData
        );
        break;

      case 'PAYMENT_FAILED':
        this.config.handlers.onPaymentFailed?.(
          eventData.data as PaymentFailedData
        );
        break;

      case 'FIELD_RESET':
        this.config.handlers.onFieldReset?.(eventData.data as FieldResetData);
        break;

      case 'SECURE_MODE_ENTERED':
        this.config.handlers.onSecureModeEntered?.(
          eventData.data as SecureModeEnteredData
        );
        break;

      case 'SESSION_UPDATED':
        this.config.handlers.onSessionUpdated?.(
          eventData.data as SessionUpdatedData
        );
        break;

      case 'SESSION_FAILED':
        this.config.handlers.onSessionFailed?.(
          eventData.data as SessionFailedData
        );
        break;

      case 'SESSION_EXPIRED':
        logger.warn('Session expired', eventData.data);
        this.config.handlers.onSessionExpired?.();
        this.disconnect();
        break;

      case 'HEARTBEAT':
        // Heartbeat - keep connection alive, no action needed
        logger.debug('SSE heartbeat received');
        break;

      case 'CONNECTION_ESTABLISHED':
        logger.info('SSE connection established', eventData.data);
        break;

      case 'TIMELINE_EVENT':
      case 'timeline_event':
        // CHAT-specific timeline event — backend sends UPPERCASE, kept lowercase for compat
        this.config.handlers.onTimelineEvent?.(
          eventData.data as TimelineEventData
        );
        break;

      default:
        logger.warn('Unknown SSE event type', { eventType, eventData });
    }
  }

  /**
   * Handle connection error and attempt reconnection
   */
  private handleConnectionError(): void {
    // The connection is dying now, not at its planned rotation time.
    if (this.rotationTimeoutId !== null) {
      window.clearTimeout(this.rotationTimeoutId);
      this.rotationTimeoutId = null;
    }

    // Clean up existing connection
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    if (this.reader) {
      this.reader.cancel().catch(() => {});
      this.reader = null;
    }

    // Don't reconnect if manually disconnected
    if (this.isManualDisconnect) {
      return;
    }

    // Don't reconnect if disabled
    if (!this.config.reconnect) {
      this.setState('failed');
      this.config.handlers.onError?.(new Error('SSE connection failed'));
      return;
    }

    // Check if max reconnect attempts reached
    if (
      this.reconnectAttempts >=
      (this.config.maxReconnectAttempts || SSE_CONFIG.MAX_RECONNECT_ATTEMPTS)
    ) {
      this.setState('failed');
      logger.error('SSE max reconnect attempts reached', {
        attempts: this.reconnectAttempts,
      });
      this.config.handlers.onError?.(new Error('SSE reconnection failed'));
      this.config.onReconnectFailed?.();
      return;
    }

    // Calculate reconnection delay with exponential backoff
    const delay = this.calculateReconnectDelay();

    this.setState('reconnecting');
    this.reconnectAttempts++;

    logger.info('SSE reconnecting', {
      attempt: this.reconnectAttempts,
      delayMs: delay,
    });

    // Schedule reconnection
    this.reconnectTimeoutId = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  /**
   * Calculate reconnection delay with exponential backoff
   *
   * Formula: min(initialDelay * (multiplier ^ attempts), maxDelay)
   *
   * Backoff sequence: 1s → 2s → 4s → 8s → 16s → 32s (max)
   *
   * @returns Delay in milliseconds
   */
  private calculateReconnectDelay(): number {
    const delay =
      SSE_CONFIG.INITIAL_RECONNECT_DELAY_MS *
      Math.pow(SSE_CONFIG.BACKOFF_MULTIPLIER, this.reconnectAttempts);

    return Math.min(delay, SSE_CONFIG.MAX_RECONNECT_DELAY_MS);
  }

  /**
   * Set connection state and notify handlers
   *
   * @param newState - New connection state
   */
  private setState(newState: SSEConnectionState): void {
    const oldState = this.state;
    this.state = newState;

    logger.debug('SSE state changed', { oldState, newState });

    // Notify handlers of state transitions
    if (newState === 'disconnected' && oldState !== 'disconnected') {
      this.config.handlers.onClose?.();
    }
  }
}
